import { httpsCallable, functions } from '@/lib/functions';
import type { TripCostConfidence, TripCostStyle } from '@/services/tripCostConfidence';

export type TripCostEstimatePayload = {
  tripId: string;
  style?: TripCostStyle;
  partySize?: number;
  bufferPct?: number;
};

export type TripCostEstimateResult = TripCostConfidence & {
  source: 'ai';
};

const callTripCostEstimate = httpsCallable<TripCostEstimatePayload, TripCostEstimateResult>(
  functions,
  'tripCostEstimate',
);

const CACHE_TTL_MS = 30 * 60 * 1000;

type CacheEntry = {
  result: TripCostEstimateResult;
  cachedAt: number;
};

const estimateCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<TripCostEstimateResult>>();

const buildCacheKey = (payload: TripCostEstimatePayload): string =>
  `${payload.tripId}:${payload.style ?? 'comfort'}:${payload.partySize ?? 1}:${payload.bufferPct ?? 12}`;

export async function requestTripCostEstimate(
  payload: TripCostEstimatePayload,
): Promise<TripCostEstimateResult> {
  const key = buildCacheKey(payload);
  const cached = estimateCache.get(key);
  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.result;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const promise = callTripCostEstimate(payload)
    .then((response) => {
      const result = response.data;
      estimateCache.set(key, { result, cachedAt: Date.now() });
      inFlight.delete(key);
      return result;
    })
    .catch((error) => {
      inFlight.delete(key);
      throw error;
    });

  inFlight.set(key, promise);
  return promise;
}

/** Test helper — clears session cache between tests. */
export function clearTripCostEstimateSessionCache(): void {
  estimateCache.clear();
  inFlight.clear();
}
